import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import ButtonsModal from '../../../../modal/ButtonsModal'
import { setIsInvoiceChangeEmail, setIsInvoiceCorrection, setSelectedInvoice, setSelectedInvoiceOrder, setShowInvoiceBillingAddressModal, setShowInvoiceCancelModal, setShowInvoiceDataModal, setShowInvoiceOptionsModal, setShowInvoiceResendEmailModal } from '../../../../../../redux/action/invoice'
import { selectContainsAnyRole, selectHasAnyGroup } from '../../../../../../redux/selector/system'
import { selectIsSelectedInvoiceCanceled, selectIsSelectedInvoicePaid, selectSelectedInvoice, selectSelectedInvoiceOrder, selectSelectedInvoiceStatusDate } from '../../../../../../redux/selector/invoice'
import { getInvoiceOrderThunk, markInvoicePaidThunk, markInvoiceUnpaidThunk } from '../../../../../../redux/thunk/invoice'
import { useCancellationWindow } from '../../../../../../hooks/useCancellationWindow'
import MainIcon from '../../../../../../img/svg/main'
import CanceledInvoiceIcon from '../CanceledInvoiceIcon'
import { isPostPaymentInvoice } from '../../constants'
import { InvoiceOptionsStatus, InvoiceOptionsStatusBar, InvoiceOptionsStatusDate, InvoiceOptionsStatusDateLabel, InvoiceOptionsStatusText } from './index.styled'

const EVENT_Z_INDEX = 80

const InvoiceOptionsModal = React.memo(({ handlePrint, onOpenOrder }) => {
	const { t } = useTranslation()
	const showOptionsModal = useSelector(store => store.invoice.modal.showOptions)
	const selectedInvoice = useSelector(selectSelectedInvoice)
	const invoiceOrder = useSelector(selectSelectedInvoiceOrder)
	const isInvoicePaid = useSelector(selectIsSelectedInvoicePaid)
	const isInvoiceCanceled = useSelector(selectIsSelectedInvoiceCanceled)
	const statusDate = useSelector(selectSelectedInvoiceStatusDate)
	const dispatch = useDispatch()
	const hasRole = useSelector(selectContainsAnyRole(['ORDERS_WRITE', 'CASH_DESK_WRITE', 'DELIVERY_SERVICE_WRITE', 'RESTAURANT_WRITE']))
	const hasRoleCancelOrder = useSelector(selectHasAnyGroup(['OWNER', 'SUPERADMIN', 'ADMIN', 'CALL_AGENT']))
	const { canCancelOrder } = useCancellationWindow()
	const orderId = selectedInvoice?.order?.orderId
	const isPhoneNumberInInvoice = selectedInvoice?.customer.phoneNumber
	const isInvoiceOrderCanceled = invoiceOrder?.status?.status === 'CANCELED'
	const isPostPayment = isPostPaymentInvoice(selectedInvoice)
	const canCancelInvoice = !isPostPayment && hasRole && hasRoleCancelOrder && !isInvoiceCanceled && !!invoiceOrder && !isInvoiceOrderCanceled && canCancelOrder(invoiceOrder)
	const statusDateLabel = isInvoiceCanceled ? 'invoices.modal.optionsModal.canceledAt' : 'invoices.modal.optionsModal.paidAt'
	const showStatusDate = (isInvoiceCanceled || isInvoicePaid) && !!statusDate
	const paymentStatusLayout = isInvoicePaid ? 'green' : 'orange'
	const statusLayout = isInvoiceCanceled ? 'canceled' : paymentStatusLayout
	const paymentStatusText = isInvoicePaid ? 'invoices.modal.invoiceDataModal.paid' : 'invoices.modal.invoiceDataModal.unpaid'
	const statusText = isInvoiceCanceled ? 'invoices.modal.invoiceDataModal.canceled' : paymentStatusText
	const disableInfoMessage = !hasRole ? { text: t('invoices.modal.optionsModal.disableMessage'), center: true } : null

	useEffect(() => {
		if (orderId) {
			dispatch(getInvoiceOrderThunk(orderId))
		}
	}, [dispatch, orderId])

	const handleClose = useCallback(() => {
		dispatch(setSelectedInvoice(null))
		dispatch(setSelectedInvoiceOrder(null))
		dispatch(setShowInvoiceOptionsModal(false))
	}, [dispatch])

	const handleShowInvoiceData = useCallback(() => {
		dispatch(setShowInvoiceOptionsModal(false))
		dispatch(setShowInvoiceDataModal(true))
	}, [dispatch])

	const handleChangeInvoiceClick = useCallback(() => {
		dispatch(setShowInvoiceOptionsModal(false))
		dispatch(setIsInvoiceCorrection(false))
		dispatch(setShowInvoiceBillingAddressModal(true))
	}, [dispatch])

	const handleResendInvoice = useCallback(() => {
		dispatch(setShowInvoiceOptionsModal(false))
		if (isPostPayment || isPhoneNumberInInvoice) {
			dispatch(setShowInvoiceResendEmailModal(true))
		} else {
			dispatch(setIsInvoiceCorrection(false))
			dispatch(setShowInvoiceBillingAddressModal(true))
			dispatch(setIsInvoiceChangeEmail(true))
		}
	}, [dispatch, isPostPayment, isPhoneNumberInInvoice])

	const handleCorrectInvoiceClick = useCallback(() => {
		dispatch(setShowInvoiceOptionsModal(false))
		dispatch(setIsInvoiceCorrection(true))
		dispatch(setShowInvoiceBillingAddressModal(true))
	}, [dispatch])

	const handleMarkPaid = useCallback(async () => {
		await dispatch(markInvoicePaidThunk(selectedInvoice.id))
		handleClose()
	}, [dispatch, selectedInvoice, handleClose])

	const handleMarkUnpaid = useCallback(async () => {
		await dispatch(markInvoiceUnpaidThunk(selectedInvoice.id))
		handleClose()
	}, [dispatch, selectedInvoice, handleClose])

	const handleCancelInvoiceClick = useCallback(() => {
		dispatch(setShowInvoiceOptionsModal(false))
		dispatch(setShowInvoiceCancelModal(true))
	}, [dispatch])

	const handleOpenOrder = useCallback(() => {
		dispatch(setShowInvoiceOptionsModal(false))
		dispatch(setSelectedInvoice(null))
		dispatch(setSelectedInvoiceOrder(null))
		onOpenOrder(invoiceOrder)
	}, [dispatch, invoiceOrder, onOpenOrder])

	const buttons = [
		{ icon: 'show', text: t('invoices.modal.optionsModal.buttons.showInvoice'), onClick: handleShowInvoiceData, show: true },
		{ icon: 'print', text: t('invoices.modal.optionsModal.buttons.printInvoice'), onClick: handlePrint, show: true },
		{ icon: 'change', text: t('invoices.modal.optionsModal.buttons.changeInvoice'), onClick: handleChangeInvoiceClick, show: !isPostPayment && !isInvoicePaid && !isInvoiceCanceled },
		{ icon: 'send-email', text: t('invoices.modal.optionsModal.buttons.sendAgainInvoice'), onClick: handleResendInvoice, show: true },
		{ icon: 'circle-checkmark-gray', text: t('invoices.modal.optionsModal.buttons.markPaidInvoice'), onClick: handleMarkPaid, show: !isPostPayment && !isInvoicePaid && !isInvoiceCanceled, disabled: !hasRole, message: disableInfoMessage },
		{ icon: 'circle-cross-gray', text: t('invoices.modal.optionsModal.buttons.markUnPaidInvoice'), onClick: handleMarkUnpaid, show: !isPostPayment && isInvoicePaid, disabled: !hasRole, message: disableInfoMessage },
		{ icon: 'change', text: t('invoices.modal.optionsModal.buttons.correctInvoice'), onClick: handleCorrectInvoiceClick, show: isPostPayment && !isInvoiceCanceled, disabled: !hasRole, message: disableInfoMessage },
		{ icon: 'cancel', text: t('invoices.modal.optionsModal.buttons.cancelInvoice'), onClick: handleCancelInvoiceClick, show: canCancelInvoice },
		{ icon: 'go-to', text: t('invoices.modal.optionsModal.buttons.goToOrder'), onClick: handleOpenOrder, show: !!onOpenOrder && !!invoiceOrder },
	]

	return (
		<ButtonsModal isOpen={showOptionsModal && selectedInvoice}
									close={handleClose}
									id="invoice-options-modal"
									title={t('invoices.modal.optionsModal.title') + ' #' + selectedInvoice?.invoiceNumber}
									titlePrice={selectedInvoice?.totalAmount}
									zIndex={EVENT_Z_INDEX}
									returnZIndex={0}
									size="small"
									buttons={buttons}>
			<InvoiceOptionsStatus>
				<InvoiceOptionsStatusBar className={statusLayout}>
					{isInvoiceCanceled ? <CanceledInvoiceIcon /> : <MainIcon icon={isInvoicePaid ? 'paidInvoice' : 'unpaidInvoice'} />}
					<InvoiceOptionsStatusText>{t(statusText)}</InvoiceOptionsStatusText>
				</InvoiceOptionsStatusBar>
				{showStatusDate &&
					<InvoiceOptionsStatusDate>
						<InvoiceOptionsStatusDateLabel>{t(statusDateLabel)}</InvoiceOptionsStatusDateLabel>
						<span>{statusDate}</span>
					</InvoiceOptionsStatusDate>}
			</InvoiceOptionsStatus>
		</ButtonsModal>
	)
})

InvoiceOptionsModal.displayName = 'InvoiceOptionsModal'
export default InvoiceOptionsModal
